/**
 * One scope-of-work line: the scannable inline row + its expandable detail panel
 * (redesign rationale §2b / §2c).
 *
 * Inline are only the columns an operator scans down a 20–150 row sheet — item,
 * category, cost type, est. total, % complete, spent, % of cost spent. The four
 * unit-math fields and the description live in the detail row, where the
 * cross-check can state its RESULT ("Qty × unit = $60,000 — differs from est.")
 * instead of leaving three cells to be compared by eye.
 *
 * Memoized on the row object: the workbook replaces only the edited row's
 * identity, so typing in one cell re-renders one row, not the whole sheet.
 *
 * Styling comes from the table-level `sx` in ScopeOfWorkWorkbook via class names,
 * so this file stays structure + behavior.
 */
import { memo, useCallback } from "react";

import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import PhotoCameraOutlined from "@mui/icons-material/PhotoCameraOutlined";

import { T, money, num, pct } from "./budgetDrawsTheme";

export const COST_CATEGORIES = ["Hard Costs", "Soft Costs"];

// Mirrors LineItem.itemCategory json_schema_extra.options on the backend model.
export const ITEM_CATEGORIES = [
  "Kitchen",
  "Bathroom",
  "HVAC",
  "Electrical",
  "Foundation",
  "Framing",
  "Windows",
  "Flooring",
  "Landscaping",
];

/** Live row math — mirrors LineItem's computed_fields for edit-time preview. */
export const lineMath = (row) => {
  const est = num(row?.estimatedTotalLineItemCost);
  const spent = num(row?.costSpentToDate);
  const byUnit =
    row?.quantityRequired && row?.unitPrice
      ? num(row.quantityRequired) * num(row.unitPrice)
      : null;
  const bySqft =
    row?.squareFootageRequired && row?.costPerSquareFoot
      ? num(row.squareFootageRequired) * num(row.costPerSquareFoot)
      : null;
  const pctSpent = est > 0 ? spent / est : 0;
  return { est, spent, byUnit, bySqft, pctSpent, reimbursed: pctSpent > 0 };
};

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

/**
 * The line-item name. Its column absorbs all the table's leftover width (see the
 * fixed layout in ScopeOfWorkWorkbook), so most names fit outright; the long
 * ones — the extract merges a description into the name on some rows — truncate
 * with an ellipsis rather than wrapping the row to three lines.
 *
 * `text-overflow: ellipsis` on an input truncates while blurred and scrolls
 * normally once focused, so the full value stays editable. The `title` puts it
 * on hover for anyone who just needs to read it.
 */
const LineItemName = ({ value, onChange }) => (
  <input
    className="item-name-in"
    value={value || ""}
    title={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder="Line item name"
    aria-label="Line item name"
  />
);

/** "Qty × unit = $X — differs from est. $Y" / "✓ matches est." / prompt. */
const CrossCheck = ({ label, computed, est }) => {
  if (computed == null) {
    return (
      <span style={{ color: T.muted }}>
        {label}: enter both to cross-check the estimate
      </span>
    );
  }
  const mismatch = Math.abs(computed - est) > 0.5;
  return (
    <span style={{ color: mismatch ? T.amber : T.green, fontWeight: mismatch ? 600 : 400 }}>
      {label} = {money(computed)}
      {mismatch ? `, differs from est. ${money(est)}` : " ✓ matches est."}
    </span>
  );
};

const SowLineRow = ({
  row,
  lineId,
  open,
  showFlagColumn,
  flash,
  photoCount = 0,
  onOpenPhotos,
  onChange,
  onToggle,
  onDelete,
}) => {
  const uid = row._uid;
  const m = lineMath(row);

  const set = useCallback(
    (field, value) => onChange(uid, field, value),
    [onChange, uid],
  );

  const onNum = useCallback(
    (field) => (e) => set(field, numOrNull(e.target.value)),
    [set],
  );

  // Percent is stored as a 0–1 fraction (LOS convention) but typed as 0–100.
  const pctDisplay =
    row.percentComplete == null ? "" : +(num(row.percentComplete) * 100).toFixed(2);

  return (
    <>
      <tr
        data-line-id={lineId || undefined}
        className={`item-row${m.reimbursed ? " reimbursed" : ""}${open ? " open" : ""}${flash ? " flash" : ""}`}
      >
        <td>
          <div className="cellpad item-cell">
            <button
              type="button"
              className="disclose"
              onClick={() => onToggle(uid)}
              aria-expanded={open}
              aria-label={open ? "Hide line details" : "Show line details"}
            >
              <KeyboardArrowRightRounded sx={{ fontSize: 16 }} />
            </button>
            <LineItemName value={row.itemName} onChange={(v) => set("itemName", v)} />
            {/* Photo evidence for this line, across every report on the loan.
                Only rendered when a report photo actually resolved to this
                line's oid — see budgets-draws-report-photos.md. */}
            {photoCount > 0 && (
              <button
                type="button"
                className="line-cam"
                onClick={() => onOpenPhotos?.(lineId)}
                title={`${photoCount} report photo${photoCount === 1 ? "" : "s"} evidence this line`}
                aria-label={`View ${photoCount} report photo${photoCount === 1 ? "" : "s"} for this line`}
              >
                <PhotoCameraOutlined sx={{ fontSize: 13 }} />
                {photoCount}
              </button>
            )}
          </div>
        </td>

        <td>
          <div className="chip-cell">
            <select
              className={`chip-sel${row.itemCategory ? " has-val" : ""}`}
              value={row.itemCategory || ""}
              onChange={(e) => set("itemCategory", e.target.value || null)}
              aria-label="Item category"
            >
              <option value="">Select Category</option>
              {ITEM_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </td>

        <td>
          <div className="chip-cell">
            <select
              className={`chip-sel ${row.costCategory === "Soft Costs" ? "soft" : "hard"}`}
              value={row.costCategory || "Hard Costs"}
              onChange={(e) => set("costCategory", e.target.value)}
              aria-label="Cost type"
            >
              {COST_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </td>

        <td className="num">
          <input
            className="cell-in"
            type="number"
            min="0"
            value={row.estimatedTotalLineItemCost ?? ""}
            onChange={onNum("estimatedTotalLineItemCost")}
            aria-label="Estimated total cost"
          />
        </td>

        <td className="num">
          <div className="pct-cell">
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={pctDisplay}
              onChange={(e) =>
                set(
                  "percentComplete",
                  e.target.value === ""
                    ? null
                    : Math.min(100, Math.max(0, Number(e.target.value) || 0)) / 100,
                )
              }
              aria-label="Percent complete"
            />
            <div className="pct-track">
              <div
                className="pct-fill"
                style={{ width: `${Math.min(100, num(row.percentComplete) * 100)}%` }}
              />
            </div>
          </div>
        </td>

        <td className="num">
          <input
            className="cell-in"
            type="number"
            min="0"
            value={row.costSpentToDate ?? ""}
            onChange={onNum("costSpentToDate")}
            aria-label="Cost spent to date"
          />
        </td>

        {/* Grey = server-computed. Recalculated here only so editing previews live. */}
        <td className="num computed">
          <div className="cellpad">{pct(m.pctSpent)}</div>
        </td>

        {/* Only rendered when SOME row on the sheet is reimbursable — otherwise
            the column is dead space, and its width belongs to the item name. */}
        {showFlagColumn && (
          <td>
            <div className="cellpad flag-cell">
              {m.reimbursed && (
                <span className="reimb-flag">
                  <span className="d" />
                  Reimburse
                </span>
              )}
            </div>
          </td>
        )}
      </tr>

      {open && (
        <tr className="detail">
          <td colSpan={showFlagColumn ? 8 : 7}>
            <div className="detail-grid">
              <div>
                <label htmlFor={`${uid}-desc`}>Description</label>
                <textarea
                  id={`${uid}-desc`}
                  value={row.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="What this line item covers"
                />
              </div>

              <div className="unit-math">
                <div className="uf">
                  <label htmlFor={`${uid}-qty`}>Qty</label>
                  <input
                    id={`${uid}-qty`}
                    type="number"
                    min="0"
                    value={row.quantityRequired ?? ""}
                    onChange={onNum("quantityRequired")}
                  />
                </div>
                <div className="uf">
                  <label htmlFor={`${uid}-unit`}>Unit price</label>
                  <input
                    id={`${uid}-unit`}
                    type="number"
                    min="0"
                    value={row.unitPrice ?? ""}
                    onChange={onNum("unitPrice")}
                  />
                </div>
                <div className="uf">
                  <label htmlFor={`${uid}-sqft`}>Sqft</label>
                  <input
                    id={`${uid}-sqft`}
                    type="number"
                    min="0"
                    value={row.squareFootageRequired ?? ""}
                    onChange={onNum("squareFootageRequired")}
                  />
                </div>
                <div className="uf">
                  <label htmlFor={`${uid}-psf`}>$ / sqft</label>
                  <input
                    id={`${uid}-psf`}
                    type="number"
                    min="0"
                    value={row.costPerSquareFoot ?? ""}
                    onChange={onNum("costPerSquareFoot")}
                  />
                </div>

                <div className="crosscheck">
                  <CrossCheck label="Qty × unit" computed={m.byUnit} est={m.est} />
                  <CrossCheck label="Sqft × $/sqft" computed={m.bySqft} est={m.est} />
                </div>

                {/* Destructive + rare — kept behind the disclosure, out of the
                    misclick path of the cells that get edited constantly. */}
                <div className="row-actions">
                  <button type="button" className="link-danger" onClick={() => onDelete(uid)}>
                    Delete line
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default memo(SowLineRow);
